import React, { useState, useEffect, useCallback } from 'react';
import { Cake, Award, PartyPopper } from 'lucide-react';
import CelebrationOverlay from './CelebrationOverlay';

const API = process.env.REACT_APP_BACKEND_URL;

export const TodaysCelebrationsWidget = () => {
  const [events, setEvents] = useState(null);
  const [demo, setDemo] = useState(null);

  const load = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) { setEvents([]); return; }
    fetch(`${API}/api/celebrations/today`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setEvents(d?.events || []))
      .catch(() => setEvents([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const celebrate = (idx) => {
    // Clicked event first, rest follow in the existing carousel
    setDemo([...events.slice(idx), ...events.slice(0, idx)]);
  };

  // BDAY-WISH-001 / ANNIV-WISH-001 — wish CTA per event type; "Wish sent ✓" once sent
  const wishable = (e) => (e.event_type === 'birthday' || e.event_type === 'anniversary') && !e.is_self;
  const buttonLabel = (e) => {
    if (!wishable(e)) return 'Celebrate 🎉';
    if (e.wished) return 'Wish sent ✓';
    return e.event_type === 'birthday' ? 'Send a wish 🎂' : 'Congratulate 🎉';
  };

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-2xl border border-slate-200/70 shadow-[0_1px_3px_rgb(0,0,0,0.04)] p-5"
         data-testid="todays-celebrations-widget">
      {demo && <CelebrationOverlay demoEvents={demo} onDemoClose={() => { setDemo(null); load(); }} />}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 text-white flex items-center justify-center shadow-md">
          <PartyPopper className="w-4 h-4" />
        </div>
        <h3 className="text-base font-bold text-slate-900">Today's Celebrations</h3>
      </div>
      {events === null ? (
        <div className="py-4 text-sm text-slate-400">Loading…</div>
      ) : events.length === 0 ? (
        <p className="text-sm text-slate-500 py-3" data-testid="no-celebrations-message">
          No birthdays or work anniversaries today.
        </p>
      ) : (
        <div className="space-y-2">
          {events.map((e, idx) => {
            const isBday = e.event_type === 'birthday';
            return (
              <div key={e.id} data-testid={`celebration-item-${e.id}`}
                   className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isBday
                    ? 'bg-pink-100 text-pink-600' : 'bg-amber-100 text-amber-600'}`}>
                    {isBday ? <Cake className="w-4 h-4" /> : <Award className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate" data-testid="celebration-item-name">{e.employee_name}</p>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${isBday
                      ? 'bg-pink-50 text-pink-700' : 'bg-amber-50 text-amber-700'}`} data-testid="celebration-item-type">
                      {isBday ? 'Birthday' : `Work Anniversary · ${e.years} ${e.years === 1 ? 'year' : 'years'}`}
                    </span>
                  </div>
                </div>
                <button onClick={() => celebrate(idx)} data-testid={`celebrate-btn-${e.id}`}
                        className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-bold text-white hover:opacity-90 hover:scale-105 transition-all duration-150 shadow-sm ${
                          e.wished && wishable(e) ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : isBday ? 'bg-gradient-to-r from-pink-500 to-purple-600' : 'bg-gradient-to-r from-amber-500 to-orange-600'}`}
                        data-wish-type={e.event_type}>
                  {buttonLabel(e)}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TodaysCelebrationsWidget;
